import { getApiBaseUrl } from './config';
import type {
  AppointmentConfirmation,
  BookingAvailabilityResponse,
  CancelBookingRequest,
  CreateBookingRequest,
  PublicBookingProfileResponse,
  RescheduleBookingRequest,
  UpdateBookingGuestDetailsRequest,
} from './types';

export class BookingApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'BookingApiError';
    this.status = status;
  }
}

interface ProblemDetails {
  detail?: string;
  title?: string;
  message?: string;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${getApiBaseUrl()}${path}`, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...init?.headers,
      },
    });
  } catch {
    throw new BookingApiError(
      'We could not reach the booking service. Please check your connection and try again.',
      0,
    );
  }

  if (!response.ok) {
    const problem = await response.json().catch(() => null) as ProblemDetails | null;
    throw new BookingApiError(
      problem?.detail || problem?.message || problem?.title || 'Something went wrong.',
      response.status,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export function fetchBookingProfile(slug: string): Promise<PublicBookingProfileResponse> {
  return request(`/v1/booking/${encodeURIComponent(slug)}`);
}

export function fetchAvailability(input: {
  slug: string;
  appointmentTypeId: string;
  fromDate?: string;
  toDate?: string;
  signal?: AbortSignal;
}): Promise<BookingAvailabilityResponse> {
  const params = new URLSearchParams({ appointmentTypeId: input.appointmentTypeId });
  if (input.fromDate) params.set('from', input.fromDate);
  if (input.toDate) params.set('to', input.toDate);

  return request(
    `/v1/booking/${encodeURIComponent(input.slug)}/availability?${params}`,
    { signal: input.signal },
  );
}

export function createBooking(
  slug: string,
  body: CreateBookingRequest,
): Promise<AppointmentConfirmation> {
  return request(`/v1/booking/${encodeURIComponent(slug)}/appointments`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function fetchAppointment(token: string): Promise<AppointmentConfirmation> {
  return request(`/v1/booking/appointments/lookup?token=${encodeURIComponent(token)}`);
}

/** Reschedule availability: the guest's own appointment is not counted busy. */
export function fetchManagedAppointmentAvailability(input: {
  token: string
  fromDate?: string
  toDate?: string
}): Promise<BookingAvailabilityResponse> {
  const params = new URLSearchParams({ token: input.token })
  if (input.fromDate) params.set('from', input.fromDate)
  if (input.toDate) params.set('to', input.toDate)

  return request(`/v1/booking/appointments/availability?${params}`)
}

export function rescheduleAppointment(
  token: string,
  body: RescheduleBookingRequest,
): Promise<AppointmentConfirmation> {
  return request(`/v1/booking/appointments/reschedule?token=${encodeURIComponent(token)}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function cancelAppointment(
  token: string,
  body: CancelBookingRequest,
): Promise<AppointmentConfirmation> {
  return request(`/v1/booking/appointments/cancel?token=${encodeURIComponent(token)}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateAppointmentDetails(
  token: string,
  body: UpdateBookingGuestDetailsRequest,
): Promise<AppointmentConfirmation> {
  return request(`/v1/booking/appointments/details?token=${encodeURIComponent(token)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}
